import React, { useState, useEffect, useCallback } from 'react'
import { first, get } from 'lodash-es'
import { useFormikContext } from 'formik'
import {
  CurrencyField,
  FormField,
  FormFieldButton,
  FundDistribution,
  Grid,
  Label,
  Segment,
  Select,
  TextInput,
} from '../../../../../components'
import { arrayUtils } from '../../../../../../utils'
import Trash from 'views/components/atoms/Svgs/Trash'
import styles from './InvoicePanel.module.scss'
import { paymentRuleClearanceTypes, rentalInvoicePaymentReferences } from 'modules/api/portfolio/constants'
import { hashCode } from 'utils/string'
import {
  defaultAgencyInvoiceTypes,
  depositInvoiceTypes,
  rentAndFeesInvoiceTypes,
  rentInvoiceTypes,
} from 'modules/api/invoice/constants'
import { head, lensPath, map as rMap, pathOr, pipe, set, sum } from 'ramda'
import TextField from 'views/components/atoms/TextField/TextField'
import FundDistributionFooter from 'views/components/organisms/FormFragments/FundDistribution/FundDistributionFooter/FundDistributionFooter'
import { PARTY_TAGS } from 'modules/api/party/constants'
import { $TSFixMe } from 'types/ts-migrate'
import { transformDepositBeneficiaryForTransferChange } from 'views/components/organisms/FormFragments/FundDistribution/DepositBeneficiary/depositBeneficiaryUtils'
import { existingInvoiceTypes, invoiceTypes } from 'utils/invoiceTypes'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'

interface InvoicePanelProps {
  agencyId: string
  ownerId: string
  invoiceTypeOptions: Array<{
    value: string
    label: string
    [key: string]: any
  }>
  invoiceParties: Array<{
    value: string
    label: string
    tag?: string
  }>
  defaultBeneficiaries: Array<Array<any>>
  getInvoiceTypeNameByValue: (value: string) => string
  invoice: any
  index: number
  toggleEdit?: (index: number) => void
  autoDeliver?: boolean
  handleInvoiceTemplateRemoved: (id: string) => void
  handleInvoiceTemplateChanged: (id: string) => void
  getPartyTagsById: (partyId: string) => string[]
  partyTags: Array<{
    value: string
    label: string
  }>
  easyPayData: any
  fetchEasyPayData: (reference: string) => void
  agencyName: string
  agencyBankDetails: any
  propertyOwnerId: string
  propertyOwnerName: string
  propertyOwnerBankDetails: any
  getPartyBankDetails?: (partyId: string) => any
  portfolioId: string
  getPartyNameById: (partyId: string) => string
  getInvoiceTypeByValue: (value: string) => any
  isCurrentAgencyGlobalVatEnabled?: boolean
  // Formik helpers
  handleChange?: (e: any) => void
  handleBlur: (e: any) => void
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void
  arrayHelpers: any
  errors: any
  isSubmitting?: boolean
  validateForm: () => Promise<any>
}

const paymentDueOptions = [
  { label: '1st of the month', value: 'BeginningOfMonth' },
  { label: '15th of the month', value: 'MiddleOfMonth' },
  { label: 'Last day of the month', value: 'EndOfMonth' },
]

const applyReferenceToPaymentRules = (
  paymentRules: any[],
  beneficiaryIndex: number | null,
  reference: any,
  type: any,
): any[] =>
  paymentRules.map((beneficiary: any, i: number) => {
    if (i !== beneficiaryIndex) return beneficiary
    return {
      ...beneficiary,
      beneficiary: {
        ...beneficiary.beneficiary,
        value: {
          ...beneficiary.beneficiary.value,
          ...(type !== 'EasyPayBeneficiary' ? { reference } : {}),
          ...(type === 'EasyPayBeneficiary' ? { easyPayReference: reference } : {}),
        },
        errors: [],
      },
    }
  })

const InvoicePanel: React.FC<InvoicePanelProps> = ({
  agencyId,
  ownerId,
  invoiceTypeOptions,
  getInvoiceTypeNameByValue,
  invoiceParties,
  defaultBeneficiaries,
  invoice,
  index,
  handleInvoiceTemplateRemoved,
  handleInvoiceTemplateChanged,
  getPartyTagsById,
  partyTags,
  easyPayData,
  fetchEasyPayData,
  agencyName,
  agencyBankDetails,
  propertyOwnerId,
  propertyOwnerName,
  propertyOwnerBankDetails,
  setFieldValue,
  handleBlur,
  errors,
  portfolioId,
  getPartyNameById,
  getInvoiceTypeByValue,
  isCurrentAgencyGlobalVatEnabled,
  setFieldTouched,
  arrayHelpers,
  isSubmitting,
  validateForm,
}) => {
  const { values } = useFormikContext()
  const namePrefix = `invoices[${index}]`
  const getCurrentPaymentRules = () => {
    const rules = invoice?.paymentRules || []
    return rules
  }

  // Hooks
  const [displayErrors, setDisplayErrors] = useState(false)
  const [fetchingEasyPayData, setFetchingEasyPayData] = useState(false)
  const [currentBeneficiaryIndex, setCurrentBeneficiaryIndex] = useState<number | null>(null)
  const [currentBeneficiaryReference, setCurrentBeneficiaryReference] = useState<string | null>(null)
  const [currentBeneficiaryType, setCurrentBeneficiaryType] = useState<string | null>(null)
  const [paymentRulesHash, setPaymentRulesHash] = useState<number | null>(() =>
    hashCode(JSON.stringify(get(invoice, 'paymentRules', ''))),
  )

  useEffect(() => {
    if (isSubmitting) {
      setDisplayErrors(true)
    }
  }, [isSubmitting])

  const onInvoiceTemplateChanged = useCallback(() => {
    const { id = false } = invoice
    id && handleInvoiceTemplateChanged(id)
  }, [invoice.id, handleInvoiceTemplateChanged])

  useEffect(() => {
    const easyPayReferenceData = currentBeneficiaryReference
      ? pathOr(null, [currentBeneficiaryReference], easyPayData)
      : null
    const easyPayError = pathOr(null, ['request', 'response_message'], easyPayReferenceData)

    if (easyPayReferenceData && fetchingEasyPayData) {
      const isEasyPayReferenceDataValid = pathOr(false, ['request', 'success'], easyPayReferenceData)
      setFetchingEasyPayData(false)

      if (isEasyPayReferenceDataValid) {
        const currentPaymentRules = getCurrentPaymentRules()
        setFieldValue(
          `${namePrefix}.paymentRules`,
          applyReferenceToPaymentRules(
            currentPaymentRules,
            currentBeneficiaryIndex,
            currentBeneficiaryReference,
            currentBeneficiaryType,
          ),
        )
        onInvoiceTemplateChanged()
      }
    }

    if (easyPayError && fetchingEasyPayData) {
      const currentPaymentRules = getCurrentPaymentRules()
      setFetchingEasyPayData(false)
      setFieldValue(
        `${namePrefix}.paymentRules`,
        currentPaymentRules.map((beneficiary: any, i: any) => {
          if (i === currentBeneficiaryIndex) {
            beneficiary = {
              ...beneficiary,
              beneficiary: {
                ...beneficiary.beneficiary,
                errors: [{ key: 'easypay_ref', message: easyPayError }],
              },
            }
          }

          return beneficiary
        }),
      )
    }
  }, [
    easyPayData,
    fetchingEasyPayData,
    currentBeneficiaryReference,
    setFieldValue,
    namePrefix,
    values,
    currentBeneficiaryIndex,
    currentBeneficiaryType,
    onInvoiceTemplateChanged,
  ])

  /**
   * Recalculates the primary owner's "Rental Income" payment rule based on the
   * current state of all payment rules and the invoice net amount.
   *
   * Rule 1: Single owner + all non-owner rules have fixed amounts → Rental Income = netAmount - sum(others)
   * Rule 2: Single owner + any non-owner rule has no amount set   → Rental Income = 0, clearance = NotSet
   * Rule 3: Multiple owners                                      → No recalculation (return unchanged)
   */
  const recalculateRentalIncomePaymentRule = (paymentRules: $TSFixMe[]): $TSFixMe[] => {
    if (invoice?.category !== 'RentInvoice') {
      return paymentRules
    }
    const isOwnerRentalIncome = ({ beneficiary }: $TSFixMe) =>
      beneficiary?.value?.partyTag === PARTY_TAGS.owner &&
      beneficiary?.value?.reference === rentalInvoicePaymentReferences.rentalIncome

    const ownerRules = paymentRules.filter(pr => pr.beneficiary?.value?.partyTag === PARTY_TAGS.owner)

    // Rule 3: Multiple owners — do nothing
    if (ownerRules.length > 1) {
      return paymentRules
    }
    // No rental income rule to recalculate
    if (!paymentRules.some(isOwnerRentalIncome)) {
      return paymentRules
    }
    const nonOwnerRules = paymentRules.filter(pr => !isOwnerRentalIncome(pr))

    const allNonOwnerAmountsSet = nonOwnerRules.every(pr => {
      const amount = pr.beneficiary?.value?.amount
      return amount !== undefined && amount !== null && amount !== '' && Number(amount) > 0
    })

    if (allNonOwnerAmountsSet) {
      // Rule 1: Single owner, all deductions fixed — calculate remainder
      const totalDeductions = nonOwnerRules.reduce(
        (acc: number, pr: $TSFixMe) => acc + (Number(pr.beneficiary?.value?.amount) || 0),
        0,
      )
      const netAmount = Number(invoice.netAmount) || 0
      const rentalIncomeAmount = Math.max(0, netAmount - totalDeductions)

      return paymentRules.map(pr =>
        isOwnerRentalIncome(pr)
          ? {
              ...pr,
              beneficiary: {
                ...pr.beneficiary,
                value: {
                  ...pr.beneficiary.value,
                  amount: rentalIncomeAmount,
                },
              },
              clearance: {
                type: paymentRuleClearanceTypes.fixed,
                value: {
                  grossAmount: rentalIncomeAmount,
                  vat: pr.beneficiary?.value?.vat ?? false,
                },
              },
            }
          : pr,
      )
    } else {
      // Rule 2: Single owner, variable/not-set rules present — reset to 0
      return paymentRules.map(pr =>
        isOwnerRentalIncome(pr)
          ? {
              ...pr,
              beneficiary: {
                ...pr.beneficiary,
                value: {
                  ...pr.beneficiary.value,
                  amount: 0,
                },
              },
              clearance: {
                type: paymentRuleClearanceTypes.notset,
                value: { notSet: true },
              },
            }
          : pr,
      )
    }
  }

  const handleNewBeneficiaryAdded = useCallback(
    (paymentRule: any, idx: number): void => {
      const currentPaymentRules = getCurrentPaymentRules()
      const vatApplied =
        existingInvoiceTypes.includes(invoice.category) &&
        paymentRule.beneficiary.value.partyTag === 'Agency' &&
        isCurrentAgencyGlobalVatEnabled
          ? true
          : false

      let paymentRulesWithNewPaymentRule: $TSFixMe = arrayUtils.insert(
        idx,
        {
          ...paymentRule,
          beneficiary: {
            ...paymentRule.beneficiary,
            value: {
              ...paymentRule.beneficiary.value,
              vat: vatApplied,
            },
          },
          clearance: {
            type: paymentRuleClearanceTypes.fixed,
            value: { grossAmount: 0, vat: false },
          },
        },
        currentPaymentRules,
      )
      paymentRulesWithNewPaymentRule = recalculateRentalIncomePaymentRule(paymentRulesWithNewPaymentRule)
      setFieldValue(`${namePrefix}.paymentRules`, paymentRulesWithNewPaymentRule)
      onInvoiceTemplateChanged()
    },
    [
      values,
      index,
      isCurrentAgencyGlobalVatEnabled,
      recalculateRentalIncomePaymentRule,
      setFieldValue,
      namePrefix,
      onInvoiceTemplateChanged,
      invoice.category,
    ],
  )

  const handleBeneficiaryRemoved = useCallback(
    (idx: number): void => {
      const currentPaymentRules = getCurrentPaymentRules()
      let updatedPaymentRules = arrayUtils.removeByIndex(idx, currentPaymentRules)
      updatedPaymentRules = recalculateRentalIncomePaymentRule(updatedPaymentRules)
      setFieldValue(`${namePrefix}.paymentRules`, updatedPaymentRules)
      onInvoiceTemplateChanged()
    },
    [setFieldValue, values, namePrefix, onInvoiceTemplateChanged],
  )

  const handleBeneficiaryAmountChange = useCallback(
    (idx: number, e: any): void => {
      const currentPaymentRules = getCurrentPaymentRules()
      setFieldValue(
        `${namePrefix}.paymentRules`,
        currentPaymentRules.map((pr: any, i: any) => {
          if (i === idx) {
            const value = get(e, ['target', 'value'], '')
            pr.clearance = pr.clearance || {
              type: paymentRuleClearanceTypes.fixed,
              value: {
                grossAmount: '',
                vat: false,
              },
            }
            pr = {
              ...pr,
              beneficiary: {
                ...pr.beneficiary,
                value: {
                  ...pr.beneficiary.value,
                  amount: value,
                },
              },
              clearance: {
                ...pr.clearance,
                value: {
                  ...pr.clearance.value,
                  grossAmount: value,
                },
              },
            }
          }
          return pr
        }),
      )
      validateForm()
      onInvoiceTemplateChanged()
    },
    [setFieldValue, values, namePrefix, validateForm, onInvoiceTemplateChanged],
  )

  const handleDepositBeneficiaryTransferChange = useCallback(
    (idx: number, transfer: any): void => {
      const currentPaymentRules = getCurrentPaymentRules()
      const paymentRule = currentPaymentRules.find((_: any, i: any) => i === idx)
      if (!paymentRule) return
      const partyName = getPartyNameById(invoice.invoicePartyId)
      const invoiceTypeObj = getInvoiceTypeByValue(invoice.category || '')
      const invoiceTypeName = pathOr('', ['name'], invoiceTypeObj)

      const transformedBeneficiary = transformDepositBeneficiaryForTransferChange(
        paymentRule,
        transfer,
        agencyId,
        partyName,
        invoiceTypeName,
        ownerId,
      )

      setFieldValue(`${namePrefix}.paymentRules[${idx}]`, transformedBeneficiary)
      onInvoiceTemplateChanged()
    },
    [
      values,
      setFieldValue,
      agencyId,
      ownerId,
      getPartyNameById,
      getInvoiceTypeByValue,
      invoice.invoicePartyId,
      invoice.category,
      namePrefix,
      onInvoiceTemplateChanged,
    ],
  )

  const handleBeneficiariesOrderChange = useCallback(
    (newOrder: any): void => {
      const currentPaymentRules = getCurrentPaymentRules()

      // Map the new order to actual beneficiaries
      const updatedPaymentRules = newOrder.map((o: any) => currentPaymentRules[o])

      // Only apply the reorder if all values are valid (no undefined)
      if (updatedPaymentRules.some((rule: any) => rule === undefined)) {
        return
      }

      setFieldValue(`${namePrefix}.paymentRules`, updatedPaymentRules)
      setPaymentRulesHash(hashCode(JSON.stringify(updatedPaymentRules)))

      // Update Redux state with the reordered values immediately
      // so captureDialogState captures the new order
      setTimeout(() => {
        onInvoiceTemplateChanged()
      }, 0)
    },
    [namePrefix, setFieldValue, onInvoiceTemplateChanged],
  )

  const onInvoiceTemplateRemoved = useCallback(
    (idx: number) => {
      const { id = false } = invoice
      id && handleInvoiceTemplateRemoved(id)
      arrayHelpers.remove(idx)
    },
    [invoice, handleInvoiceTemplateRemoved, arrayHelpers],
  )

  const getDefaultPartySearchResults = useCallback(
    (beneficiaryPartyTag: any) => {
      const invoicePartyTag = getPartyTagsById(invoice.invoicePartyId)
      const newDefaults = get(defaultBeneficiaries, '[0]', [])

      if (beneficiaryPartyTag === 'Owner') {
        return [newDefaults.filter((b: any) => b.partyTag !== 'agency')]
      } else if (first(invoicePartyTag) === 'Owner') {
        return [newDefaults.filter((b: any) => b.partyTag !== 'Owner')]
      }

      return defaultBeneficiaries
    },
    [invoice, getPartyTagsById, defaultBeneficiaries],
  )

  const getPaymentRuleAmountsSum = useCallback(
    () => pipe(rMap(pathOr(0, ['beneficiary', 'value', 'amount'])), sum)(invoice.paymentRules),
    [invoice],
  )

  const handleInvoiceCategoryChange = useCallback(
    (data: any, categoryName: any): void => {
      onInvoiceTemplateChanged()
      setFieldValue(categoryName, data.value)

      if (invoiceTypes.includes(data.value) && isCurrentAgencyGlobalVatEnabled) {
        setFieldValue(`${namePrefix}.vatable`, true)
      } else {
        setFieldValue(`${namePrefix}.vatable`, false)
      }

      if (depositInvoiceTypes.includes(data.value)) {
        const beneficiary = {
          beneficiary: {
            type: 'DepositBeneficiary',
            value: {
              amount: 0,
              beneficiaryTag: 'TenantDepositAccount',
              reference: 'Transfer to managed deposit wallet on reOS',
              transfer: true,
              vat: false,
            },
          },
        }
        setFieldValue(`${namePrefix}.paymentRules`, [beneficiary])
      } else if (defaultAgencyInvoiceTypes.includes(data.value)) {
        const beneficiary = {
          beneficiary: {
            type: 'PartyBeneficiary',
            value: {
              amount: 0,
              name: agencyName,
              partyId: agencyId,
              partyTag: 'Agency',
              reference: `${data.label}`,
              vat: isCurrentAgencyGlobalVatEnabled,
            },
          },
        }
        setFieldValue(`${namePrefix}.paymentRules`, [beneficiary])
      } else if (
        invoice.paymentRules.some((b: any) => b.beneficiary.partyTag === 'Agency') ||
        !defaultAgencyInvoiceTypes.includes(data.value)
      ) {
        setFieldValue(`${namePrefix}.paymentRules`, [])
      } else if (invoice.paymentRules.some((b: any) => b.beneficiary.type === 'DepositBeneficiary')) {
        setFieldValue(`${namePrefix}.paymentRules`, [])
      }
    },
    [
      setFieldValue,
      invoice,
      agencyId,
      agencyName,
      isCurrentAgencyGlobalVatEnabled,
      namePrefix,
      onInvoiceTemplateChanged,
    ],
  )

  const handleAmountChange = useCallback(
    (e: any): void => {
      const amount = e.target.value
      setFieldValue(`${namePrefix}.netAmount`, amount)
      if (depositInvoiceTypes.includes(invoice.category)) {
        const paymentRule = head(invoice.paymentRules)
        const updatedPaymentRule = set(lensPath(['beneficiary', 'value', 'amount']), amount, paymentRule)
        setFieldValue(`${namePrefix}.paymentRules[0]`, updatedPaymentRule)
      }
      onInvoiceTemplateChanged()
    },
    [setFieldValue, invoice, namePrefix, onInvoiceTemplateChanged],
  )

  const handleDescriptionBlur = useCallback(
    ({ target }: any) => {
      setFieldValue(`${namePrefix}.description`, target.value)
      onInvoiceTemplateChanged()
    },
    [setFieldValue, namePrefix, onInvoiceTemplateChanged],
  )

  const handleDescriptionChange = useCallback(
    ({ target }: any) => {
      setFieldValue(`${namePrefix}.description`, target.value)
    },
    [setFieldValue, namePrefix],
  )

  const handleBeneficiaryReferenceChange = useCallback(
    (beneficiaryIndex: number | null, reference: any, type: any): void => {
      const currentPaymentRules = getCurrentPaymentRules()
      setFieldValue(
        `${namePrefix}.paymentRules`,
        applyReferenceToPaymentRules(currentPaymentRules, beneficiaryIndex, reference, type),
      )

      if (type === 'EasyPayBeneficiary') {
        const easyPayReferenceData = pathOr(null, [reference], easyPayData)
        const isEasyPayReferenceDataValid = pathOr(false, ['request', 'success'], easyPayReferenceData)

        if (!easyPayReferenceData || !isEasyPayReferenceDataValid) {
          setFetchingEasyPayData(true)
          setCurrentBeneficiaryIndex(beneficiaryIndex)
          setCurrentBeneficiaryReference(reference)
          setCurrentBeneficiaryType(type)
          fetchEasyPayData(reference)
        } else {
          onInvoiceTemplateChanged()
        }
      } else {
        onInvoiceTemplateChanged()
      }
    },
    [fetchEasyPayData, easyPayData, setFieldValue, values, namePrefix, onInvoiceTemplateChanged],
  )

  const getBeneficiariesAllocationsSum = useCallback(
    () => pipe(pathOr([], ['paymentRules']), rMap(pathOr(0, ['beneficiary', 'value', 'amount'])), sum)(invoice),
    [invoice],
  )

  const getUnallocatedAmount = useCallback(() => {
    const netAmount = pathOr(null, ['netAmount'], invoice)
    return netAmount ? netAmount - getBeneficiariesAllocationsSum() || 0 : 0
  }, [invoice, getBeneficiariesAllocationsSum])

  const handlePaymentDueChange = useCallback(
    (data: any): void => {
      setFieldValue(`${namePrefix}.interval`, data.value)
      onInvoiceTemplateChanged()
    },
    [setFieldValue, namePrefix, onInvoiceTemplateChanged],
  )

  const buildFundDistributionFooter = useCallback(() => {
    const netAmount = pathOr(0, ['netAmount'], invoice)
    return (
      <>
        <FundDistributionFooter
          text={'Amount Unallocated'}
          amount={getUnallocatedAmount()}
          isComplete={getUnallocatedAmount() === 0}
          progress={{
            value: 100 - ((netAmount - getBeneficiariesAllocationsSum()) / netAmount) * 100,
            style: 'default',
          }}
        />
        {invoice.interval !== 'OnceOff' && (
          <Segment className={styles['payment-due']}>
            <Select
              isSearchable={false}
              name={`${namePrefix}.interval`}
              label="Payment due"
              value={paymentDueOptions.find((option: any) => option.value === invoice.interval)}
              options={paymentDueOptions}
              onChange={handlePaymentDueChange}
            />
          </Segment>
        )}
      </>
    )
  }, [invoice, getUnallocatedAmount, getBeneficiariesAllocationsSum, namePrefix, handlePaymentDueChange])

  const categoryName = `${namePrefix}.category`
  // The "Rent & fees" step only ever charges the administration fee once-off, so a recurring one was
  // added by the user here and stays editable.
  const isRecurringAdministrationFee = invoice.category === 'AdministrationFee' && invoice.interval !== 'OnceOff'
  // Auto-generated = any template the "Rent & fees" step produces (rent, deposits, fees), unless it's a
  // user-added empty template (isEmpty). These are system-managed: read-only "Invoice to", locked amount, no delete.
  const isAutoGenerated =
    rentAndFeesInvoiceTypes.includes(invoice.category) && !invoice.isEmpty && !isRecurringAdministrationFee
  /** A deposit's payment rule amount is derived from the invoice amount, see `handleAmountChange` */
  const isDepositCategory = depositInvoiceTypes.includes(invoice.category)
  const isRentCategory = rentInvoiceTypes.includes(invoice.category)
  const invoiceType = {
    name: getInvoiceTypeNameByValue(invoice.category),
    value: invoice.category,
  }

  return (
    <div className={styles.root}>
      <Segment.Group key={index}>
        <Segment.Group horizontal>
          {/* @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element; style: { width: string;... Remove this comment to see the full error message */}
          <Segment style={{ width: '85%' }}>
            {isAutoGenerated ? (
              <div className={styles.header}>
                <h3 style={{ fontSize: '20px' }}>{invoiceType.name}</h3>
                <Label circular className={styles.badge}>
                  Auto-generated
                </Label>
              </div>
            ) : (
              <Select
                isSearchable
                name={categoryName}
                placeholder="Select invoice type"
                value={invoiceTypeOptions.find((type: any) => type.value === invoice.category)}
                error={displayErrors && errors.category}
                onChange={(data: any) => handleInvoiceCategoryChange(data, categoryName)}
                onBlur={handleBlur}
                options={invoiceTypeOptions}
              />
            )}
          </Segment>
          {!isAutoGenerated && (
            <Segment>
              <FormFieldButton state="danger" onClick={() => onInvoiceTemplateRemoved(index)}>
                <Trash width="34px" height="34px" />
              </FormFieldButton>
            </Segment>
          )}
        </Segment.Group>

        <Segment.Group>
          <Segment horizontal>
            <Grid>
              <Grid.Column xs={12} md={6} className={styles['invoice-party-column']}>
                <FormField>
                  <Select
                    isSearchable={!isAutoGenerated}
                    isDisabled={isAutoGenerated}
                    name={`${namePrefix}.invoiceParty`}
                    value={invoiceParties.find((party: any) => party.value === invoice.invoicePartyId)}
                    label="Invoice to"
                    error={displayErrors && errors.invoicePartyId}
                    options={invoiceParties}
                    onBlur={() => setFieldTouched(`${namePrefix}.invoicePartyId`)}
                    onChange={(data: any) => {
                      setFieldValue(`${namePrefix}.invoicePartyAccountType`, data.tag)
                      setFieldValue(`${namePrefix}.invoicePartyId`, data.value)
                      onInvoiceTemplateChanged()
                    }}
                  />
                </FormField>
              </Grid.Column>

              <Grid.Column split xs={12} md={6}>
                <FormField>
                  <CurrencyField
                    includeVatControl
                    disabled={isAutoGenerated}
                    isEditVatDisabled={isDepositCategory || (isAutoGenerated && !isRentCategory)}
                    vatApplied={invoice.vatable}
                    name={`${namePrefix}.netAmount`}
                    label={
                      isAutoGenerated
                        ? `Amount ${invoice.vatable ? '(incl. VAT)' : ''}`
                        : `Amount (optional)${invoice.vatable ? ' (incl. VAT)' : ''}`
                    }
                    labelHint={isAutoGenerated ? undefined : 'Leave blank for variable amounts.'}
                    placeholder={isAutoGenerated ? 'Set on send' : 'Variable'}
                    value={invoice.netAmount}
                    error={displayErrors && errors.netAmount}
                    onChange={handleAmountChange}
                    onToggleChange={(vatApplied: any) => {
                      setFieldValue(`${namePrefix}.vatable`, vatApplied)
                      onInvoiceTemplateChanged()
                    }}
                  />
                </FormField>
              </Grid.Column>
            </Grid>
          </Segment>
          <Segment horizontal>
            <TextField
              inputComponent={
                <TextInput
                  type={TextFieldTypes.text}
                  value={invoice.description}
                  onBlur={handleDescriptionBlur}
                  onChange={handleDescriptionChange}
                />
              }
              label="Description"
              labelHint={
                isAutoGenerated
                  ? undefined
                  : 'This description will appear on every generated invoice. Keep it generic by avoiding month or date references.'
              }
            />
          </Segment>
        </Segment.Group>

        <FundDistribution
          state={displayErrors && errors.paymentRules ? 'danger' : 'default'}
          errors={displayErrors ? { beneficiaries: errors.paymentRules } : undefined}
          headerText="Payment Allocation"
          invoiceType={invoiceType}
          invoiceStatus={null}
          balance={parseFloat(invoice.netAmount)}
          onNewBeneficiaryAdded={handleNewBeneficiaryAdded}
          onBeneficiaryRemoved={handleBeneficiaryRemoved}
          onNewBeneficiaryAmountChange={handleBeneficiaryAmountChange}
          onBeneficiaryReferenceChange={handleBeneficiaryReferenceChange}
          onDepositBeneficiaryTransferChange={handleDepositBeneficiaryTransferChange}
          onBeneficiariesOrderChange={handleBeneficiariesOrderChange}
          beneficiaries={getCurrentPaymentRules()}
          partySearchTags=""
          partyTags={partyTags}
          getDefaultResultSets={getDefaultPartySearchResults}
          beneficiaryAmountPlaceholder="Set on send"
          onVatChange={(i, vat, amount) => {
            setFieldValue(`${namePrefix}.paymentRules[${i}].beneficiary.value.vat`, vat)
          }}
          Footer={buildFundDistributionFooter}
          easyPayData={easyPayData}
          fetchEasyPayData={fetchEasyPayData}
          beneficiariesKey={paymentRulesHash?.toString() || null}
          invoiceVatEnabled={invoice.vatable}
          propertyOwnerId={propertyOwnerId}
          propertyOwnerName={propertyOwnerName}
          propertyOwnerBankDetails={propertyOwnerBankDetails}
          unallocatedAmount={parseFloat(invoice.netAmount) - getPaymentRuleAmountsSum() || 0}
          portfolioId={portfolioId}
          isDepositAmountDerivedFromInvoice={isDepositCategory}
        />
      </Segment.Group>
    </div>
  )
}

export default InvoicePanel
